import { useState } from "react";
import { Download, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const input =
  "w-full bg-[#050505] px-3 py-2 text-xs text-white outline-none ring-1 ring-zinc-800 focus:ring-[#00ffcc]";

export const WaitlistTab = ({ waitlist, productName, onChanged }) => {
  const [query, setQuery] = useState("");
  const [deleting, setDeleting] = useState(null);

  const filtered = waitlist.filter((w) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return w.email.toLowerCase().includes(q) || productName(w.product_id).toLowerCase().includes(q);
  });

  const exportCsv = () => {
    const rows = [
      ["email", "source", "signed_up"],
      ...filtered.map((w) => [
        w.email,
        productName(w.product_id),
        w.created_at ? new Date(w.created_at).toISOString() : "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (entry) => {
    if (!entry?.id) return toast.error("This entry has no id — refresh and try again");
    if (!window.confirm(`Remove ${entry.email} from the waitlist?`)) return;
    setDeleting(entry.id);
    try {
      await api.delete(`/admin/waitlist/${entry.id}`);
      toast.success("Removed from waitlist");
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="mt-10" data-testid="waitlist-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-sm uppercase tracking-[0.2em]">
            <Users className="h-4 w-4 text-[#00ffcc]" /> Waitlist
            <span className="text-zinc-500">· {waitlist.length}</span>
          </h2>
          <p className="mt-2 text-[10px] leading-relaxed text-zinc-600">
            Everyone who asked to be notified when a coming-soon drop goes live.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="waitlist-search-input"
            className={`${input} w-52`}
            placeholder="Search email or product"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            data-testid="waitlist-export-btn"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 border border-[#00ffcc] px-4 text-[10px] uppercase tracking-[0.25em] text-[#00ffcc] transition-colors hover:bg-[#00ffcc] hover:text-black disabled:opacity-40"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-3" data-testid="waitlist-list">
        {filtered.length === 0 && (
          <p data-testid="waitlist-empty" className="text-xs text-zinc-600">
            No waitlist signups yet.
          </p>
        )}
        {filtered.map((w) => (
          <div
            key={w.id || `${w.email}-${w.product_id}`}
            data-testid={`waitlist-row-${w.email}`}
            className="flex flex-wrap items-center justify-between gap-3 border border-[#1f1f1f] bg-[#0a0a0a] p-4"
          >
            <div>
              <p className="font-mono text-xs text-white">{w.email}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {productName(w.product_id)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                {w.created_at ? new Date(w.created_at).toLocaleString() : "—"}
              </span>
              <button
                type="button"
                data-testid={`waitlist-delete-${w.id || w.email}`}
                title="Remove from waitlist"
                disabled={deleting === w.id}
                onClick={() => remove(w)}
                className="border border-zinc-700 p-2 text-zinc-500 transition-colors hover:border-[#ff3b30] hover:text-[#ff3b30] disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
